// frontend/src/pages/settings/UserManagement.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSchool } from '../../context/SchoolContext';
import api from '../../utils/axios';
import ResetPasswordModal from './ResetPasswordModal';
import PageHeader from '../../components/PageHeader';

const UserManagement = ({ embedded = false }) => {
  const { user: currentUser } = useAuth();
  const { school } = useSchool();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('list'); // 'list' | 'create'

  // List & Filter states
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successBanner, setSuccessBanner] = useState('');

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 1 });

  // Modal states
  const [resetModalUser, setResetModalUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);

  // Form states for Create User
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: currentUser?.role === 'super_admin' ? 'principal' : 'admin',
    school_id: '',
    is_active: true,
    student_id: ''
  });
  const [students, setStudents] = useState([]);
  const [schoolsList, setSchoolsList] = useState([]);
  const [schoolFilter, setSchoolFilter] = useState('all');
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [createdSummary, setCreatedSummary] = useState(null);

  useEffect(() => {
    if (currentUser?.role === 'super_admin') {
      api.get('/api/v1/settings/all-schools')
        .then((res) => {
          if (res.data?.success) {
            setSchoolsList(res.data.data || []);
          }
        })
        .catch(console.error);
    } else if (currentUser) {
      setFormData((prev) => ({ ...prev, role: 'admin' }));
    }
  }, [currentUser]);

  // Edit User Form state
  const [editFormData, setEditFormData] = useState({
    name: '',
    role: '',
    is_active: true,
    student_id: ''
  });

  /**
   * Fetch paginated users list from API
   */
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/api/v1/users', {
        params: {
          search,
          role: roleFilter,
          is_active: statusFilter,
          school_id: schoolFilter,
          page,
          limit: 10
        }
      });

      if (response.data?.success) {
        setUsers(response.data.data || []);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.message || 'Failed to load user accounts');
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, statusFilter, schoolFilter, page]);

  /**
   * Fetch students list for Parent role student dropdown
   */
  const fetchStudents = async () => {
    try {
      const res = await api.get('/api/v1/students', { params: { limit: 1000 } });
      if (res.data?.success) {
        setStudents(res.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching students list:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (activeTab === 'create' || editingUser) {
      fetchStudents();
    }
  }, [activeTab, editingUser]);

  // Handle Form Change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Create User Submit
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setCreatedSummary(null);

    if (!formData.name.trim() || !formData.email.trim()) {
      setError('Name and Email are required.');
      return;
    }

    if (!formData.password || formData.password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      setFormSubmitting(true);
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        password: formData.password,
        role: formData.role,
        school_id: currentUser?.role === 'super_admin' && formData.school_id ? parseInt(formData.school_id, 10) : undefined,
        is_active: formData.is_active ? 1 : 0,
        student_id: formData.role === 'parent' && formData.student_id ? parseInt(formData.student_id) : null
      };

      const res = await api.post('/api/v1/users', payload);
      if (res.data?.success) {
        setCreatedSummary({
          name: res.data.data.name,
          email: res.data.data.email,
          role: res.data.data.role,
          password: formData.password
        });
        setFormData({
          name: '',
          email: '',
          password: '',
          confirmPassword: '',
          role: 'principal',
          school_id: '',
          is_active: true,
          student_id: ''
        });
        fetchUsers();
      }
    } catch (err) {
      console.error('Error creating user:', err);
      const msg = err.response?.data?.message || (err.code === 'ECONNABORTED' ? 'Request timed out. Please try again.' : 'Failed to create user account.');
      setError(msg);
    } finally {
      setFormSubmitting(false);
    }
  };

  // Toggle user status
  const handleToggleStatus = async (user) => {
    try {
      const res = await api.put(`/api/v1/users/${user.id}/status`);
      if (res.data?.success) {
        setSuccessBanner(`Status for ${user.name} changed successfully.`);
        fetchUsers();
        setTimeout(() => setSuccessBanner(''), 3000);
      }
    } catch (err) {
      console.error('Error toggling status:', err);
      alert(err.response?.data?.message || 'Failed to update user status.');
    }
  };

  // Open Edit User Modal
  const openEditModal = (user) => {
    setEditingUser(user);
    setEditFormData({
      name: user.name || '',
      email: user.email || '',
      role: user.role || 'principal',
      school_id: user.school_id || '',
      is_active: user.is_active === 1,
      student_id: user.student_id || ''
    });
  };

  // Submit Edit User
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingUser) return;

    try {
      const payload = {
        name: editFormData.name.trim(),
        email: editFormData.email.trim().toLowerCase(),
        role: editFormData.role,
        school_id: editFormData.school_id ? parseInt(editFormData.school_id, 10) : undefined,
        is_active: editFormData.is_active ? 1 : 0,
        student_id: editFormData.role === 'parent' && editFormData.student_id ? parseInt(editFormData.student_id) : null
      };

      const res = await api.put(`/api/v1/users/${editingUser.id}`, payload);
      if (res.data?.success) {
        setSuccessBanner(`User ${editFormData.name} updated successfully.`);
        setEditingUser(null);
        fetchUsers();
        setTimeout(() => setSuccessBanner(''), 3000);
      }
    } catch (err) {
      console.error('Error updating user:', err);
      alert(err.response?.data?.message || 'Failed to update user.');
    }
  };

  // Delete User
  const handleDeleteUser = async () => {
    if (!deletingUser) return;
    try {
      const res = await api.delete(`/api/v1/users/${deletingUser.id}`);
      if (res.data?.success) {
        setSuccessBanner(`User ${deletingUser.name} deleted successfully.`);
        setDeletingUser(null);
        fetchUsers();
        setTimeout(() => setSuccessBanner(''), 3000);
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      alert(err.response?.data?.message || 'Failed to delete user.');
    }
  };

  // Role Badge Styling
  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'principal': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'admin': return 'bg-teal-100 text-teal-800 border-teal-200';
      case 'teacher': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'accountant': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'parent': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'student': return 'bg-slate-100 text-slate-700 border-slate-200';
      case 'librarian': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  // Helper to verify if current user can modify target user's account based on role hierarchy
  const canManageTargetUser = (targetRole) => {
    const curRole = currentUser?.role?.toLowerCase();
    const tgtRole = targetRole?.toLowerCase();

    if (curRole === 'super_admin') return true;
    if (curRole === 'principal') {
      return tgtRole !== 'super_admin' && tgtRole !== 'principal';
    }
    if (curRole === 'admin') {
      return tgtRole !== 'super_admin' && tgtRole !== 'principal' && tgtRole !== 'admin';
    }
    return false;
  };

  // Roles list based on current user logged in
  const availableRoles = currentUser?.role === 'principal'
    ? [
        { value: 'admin', label: 'Admin' },
        { value: 'teacher', label: 'Teacher' },
        { value: 'accountant', label: 'Accountant' },
        { value: 'librarian', label: 'Librarian' },
        { value: 'parent', label: 'Parent' },
        { value: 'student', label: 'Student' }
      ]
    : [
        { value: 'super_admin', label: 'Super Admin' },
        { value: 'principal', label: 'Principal' },
        { value: 'admin', label: 'Admin' },
        { value: 'teacher', label: 'Teacher' },
        { value: 'accountant', label: 'Accountant' },
        { value: 'librarian', label: 'Librarian' },
        { value: 'parent', label: 'Parent' },
        { value: 'student', label: 'Student' }
      ];

  return (
    <div className={embedded ? 'space-y-6' : 'min-h-screen bg-slate-50 flex flex-col'}>
      {/* Page Navigation Header */}
      {!embedded && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <PageHeader
            title="User Management & Roles"
            subtitle={`${school?.name || 'School System'} User Accounts & Role Permissions`}
            breadcrumbs={[
              { label: 'Settings', path: '/settings' },
              { label: 'User Management' }
            ]}
            backUrl="/settings"
          >
            <button
              onClick={() => navigate('/settings')}
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium px-3.5 py-1.5 rounded-xl text-xs transition border border-slate-200 flex items-center gap-1.5"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              </svg>
              <span>School Settings</span>
            </button>
          </PageHeader>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 pb-8">

        {/* Global Banners */}
        {successBanner && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-2xl flex items-center justify-between shadow-sm animate-fadeIn">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-medium">{successBanner}</span>
            </div>
            <button onClick={() => setSuccessBanner('')} className="text-emerald-500 hover:text-emerald-700">
              &times;
            </button>
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="flex items-center justify-between border-b border-slate-200 mb-6">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setActiveTab('list')}
              className={`px-5 py-3 text-sm font-semibold border-b-2 transition duration-150 flex items-center gap-2 ${
                activeTab === 'list'
                  ? 'border-primary text-primary bg-blue-50/50 rounded-t-xl'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <span>All Users</span>
              <span className="ml-1.5 px-2 py-0.5 text-xs bg-slate-200 text-slate-700 rounded-full font-bold">
                {pagination.total}
              </span>
            </button>

            {!embedded && (
              <button
                onClick={() => setActiveTab('create')}
                className={`px-5 py-3 text-sm font-semibold border-b-2 transition duration-150 flex items-center gap-2 ${
                  activeTab === 'create'
                    ? 'border-primary text-primary bg-blue-50/50 rounded-t-xl'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                <span>Create User</span>
              </button>
            )}
          </div>
        </div>

        {/* TAB 1: ALL USERS LIST */}
        {activeTab === 'list' && (
          <div className="space-y-6">
            
            {/* Filter Bar */}
            <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
              
              {/* Search */}
              <div className="relative flex-1">
                <svg className="w-4 h-4 absolute left-3.5 top-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Search by name or email address..."
                  className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
                />
              </div>

              {/* Role & Status Dropdowns */}
              <div className="flex flex-wrap items-center gap-3">
                
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-500">Role:</span>
                  <select
                    value={roleFilter}
                    onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
                    className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-700 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 transition"
                  >
                    <option value="all">All Roles</option>
                    <option value="super_admin">Super Admin</option>
                    <option value="principal">Principal</option>
                    <option value="admin">Admin</option>
                    <option value="teacher">Teacher</option>
                    <option value="accountant">Accountant</option>
                    <option value="librarian">Librarian</option>
                    <option value="parent">Parent</option>
                    <option value="student">Student</option>
                  </select>
                </div>

                {currentUser?.role === 'super_admin' && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-500">School:</span>
                    <select
                      value={schoolFilter}
                      onChange={(e) => { setSchoolFilter(e.target.value); setPage(1); }}
                      className="bg-purple-50 border border-purple-200 rounded-xl px-3 py-2 text-xs font-extrabold text-purple-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition"
                    >
                      <option value="all">All Tenant Schools</option>
                      {schoolsList.map((s) => (
                        <option key={s.id} value={s.id}>
                          #{s.id} {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-500">Status:</span>
                  <select
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                    className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-700 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 transition"
                  >
                    <option value="all">All Status</option>
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>

              </div>

            </div>

            {/* Users Table */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              {loading ? (
                <div className="p-12 text-center">
                  <div className="inline-block w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin mb-3"></div>
                  <p className="text-sm font-medium text-slate-500">Loading user accounts...</p>
                </div>
              ) : users.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <h4 className="font-bold text-slate-700 text-base">No Users Found</h4>
                  <p className="text-xs text-slate-500 mt-1">Try adjusting your search query or role/status filters.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50/80 border-b border-slate-200 text-slate-600 text-xs font-bold uppercase tracking-wider">
                        <th className="py-3.5 px-4 sm:px-6">Name</th>
                        <th className="py-3.5 px-4 sm:px-6">Email Address</th>
                        <th className="py-3.5 px-4 sm:px-6">Role</th>
                        <th className="py-3.5 px-4 sm:px-6">Status</th>
                        <th className="py-3.5 px-4 sm:px-6">Last Login</th>
                        <th className="py-3.5 px-4 sm:px-6 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-sm">
                      {users.map((u) => (
                        <tr key={u.id} className="hover:bg-slate-50/60 transition">
                          
                          {/* Name */}
                          <td className="py-4 px-4 sm:px-6 font-semibold text-slate-800">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center font-bold text-xs">
                                {u.name ? u.name.charAt(0).toUpperCase() : 'U'}
                              </div>
                              <span>{u.name}</span>
                            </div>
                          </td>

                          {/* Email */}
                          <td className="py-4 px-4 sm:px-6 text-slate-600 font-mono text-xs">
                            {u.email}
                          </td>

                          {/* Role Badge */}
                          <td className="py-4 px-4 sm:px-6">
                            <span className={`inline-block px-2.5 py-1 text-xs font-bold rounded-lg border capitalize ${getRoleBadgeClass(u.role)}`}>
                              {u.role ? u.role.replace('_', ' ') : 'User'}
                            </span>
                          </td>

                          {/* Status Badge */}
                          <td className="py-4 px-4 sm:px-6">
                            {u.is_active === 1 ? (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold rounded-lg bg-red-50 text-red-700 border border-red-200">
                                <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                                Inactive
                              </span>
                            )}
                          </td>

                          {/* Last Login */}
                          <td className="py-4 px-4 sm:px-6 text-slate-500 text-xs">
                            {u.last_login ? new Date(u.last_login).toLocaleString() : 'Never'}
                          </td>

                          {/* Actions */}
                          <td className="py-4 px-4 sm:px-6 text-right">
                            {canManageTargetUser(u.role) ? (
                              <div className="flex items-center justify-end gap-1.5">
                                
                                {/* Edit */}
                                <button
                                  onClick={() => openEditModal(u)}
                                  className="p-1.5 rounded-lg text-slate-600 hover:text-primary hover:bg-blue-50 transition"
                                  title="Edit User"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>

                                {/* Reset Password */}
                                <button
                                  onClick={() => setResetModalUser(u)}
                                  className="p-1.5 rounded-lg text-slate-600 hover:text-amber-600 hover:bg-amber-50 transition"
                                  title="Reset Password"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                  </svg>
                                </button>

                                {/* Toggle Status */}
                                <button
                                  onClick={() => handleToggleStatus(u)}
                                  disabled={u.id === currentUser?.id}
                                  className={`p-1.5 rounded-lg transition ${
                                    u.id === currentUser?.id
                                      ? 'opacity-40 cursor-not-allowed text-slate-400'
                                      : u.is_active === 1
                                      ? 'text-slate-600 hover:text-red-600 hover:bg-red-50'
                                      : 'text-slate-600 hover:text-emerald-600 hover:bg-emerald-50'
                                  }`}
                                  title={u.is_active === 1 ? 'Deactivate User' : 'Activate User'}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                  </svg>
                                </button>

                                {/* Delete (Super Admin only) */}
                                {currentUser?.role === 'super_admin' && (
                                  <button
                                    onClick={() => setDeletingUser(u)}
                                    disabled={u.id === currentUser?.id}
                                    className={`p-1.5 rounded-lg transition ${
                                      u.id === currentUser?.id
                                        ? 'opacity-40 cursor-not-allowed text-slate-400'
                                        : 'text-slate-600 hover:text-red-600 hover:bg-red-50'
                                    }`}
                                    title="Delete User"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span className="inline-block px-2.5 py-1 text-[11px] font-semibold text-slate-400 bg-slate-100 rounded-lg italic">
                                Protected
                              </span>
                            )}
                          </td>

                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination Bar */}
              {pagination.totalPages > 1 && (
                <div className="bg-slate-50/80 border-t border-slate-200 px-6 py-3 flex items-center justify-between">
                  <span className="text-xs text-slate-500 font-medium">
                    Showing Page <span className="font-bold text-slate-800">{pagination.page}</span> of{' '}
                    <span className="font-bold text-slate-800">{pagination.totalPages}</span> ({pagination.total} total)
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                      disabled={page >= pagination.totalPages}
                      className="px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>
        )}

        {/* TAB 2: CREATE USER FORM */}
        {activeTab === 'create' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-200 shadow-sm">
              <div className="mb-6">
                <h3 className="text-xl font-bold text-slate-800">Create New User Account</h3>
                <p className="text-xs text-slate-500 mt-1">
                  Add a new staff, administrator, teacher, parent, or student user to your school directory.
                </p>
              </div>

              {/* Error Banner */}
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl flex items-center gap-2.5">
                  <svg className="w-5 h-5 shrink-0 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{error}</span>
                </div>
              )}

              {/* Success Credentials Summary Banner */}
              {createdSummary && (
                <div className="mb-6 p-5 bg-emerald-50 border border-emerald-200 rounded-2xl shadow-sm space-y-3">
                  <div className="flex items-center gap-2 text-emerald-800 font-bold text-sm">
                    <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>User Account Created Successfully!</span>
                  </div>
                  <div className="bg-white p-3.5 rounded-xl border border-emerald-200 text-xs font-mono space-y-1 text-slate-700">
                    <p><span className="font-bold text-slate-900">Name:</span> {createdSummary.name}</p>
                    <p><span className="font-bold text-slate-900">Email:</span> {createdSummary.email}</p>
                    <p><span className="font-bold text-slate-900">Password:</span> {createdSummary.password}</p>
                    <p><span className="font-bold text-slate-900">Role:</span> <span className="capitalize">{createdSummary.role}</span></p>
                  </div>
                  <p className="text-xs text-emerald-700">Please share these login credentials securely with the user.</p>
                </div>
              )}

              <form onSubmit={handleCreateSubmit} className="space-y-5">
                
                {/* Full Name */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g. Sarah Jenkins"
                    required
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
                  />
                </div>

                {/* Email Address */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="e.g. sarah.j@school.com"
                    required
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
                  />
                </div>

                {/* Password Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                      Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Min. 8 characters"
                      required
                      minLength={8}
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                      Confirm Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="Re-enter password"
                      required
                      minLength={8}
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
                    />
                  </div>
                </div>

                {/* Role Dropdown */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition capitalize"
                  >
                    {availableRoles.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Target Tenant School Dropdown (Only for Super Admin) */}
                {currentUser?.role === 'super_admin' && (
                  <div className="p-4 bg-purple-50/70 border border-purple-200 rounded-2xl animate-fadeIn space-y-1">
                    <label className="block text-xs font-extrabold text-purple-900 mb-1">
                      Assign to Client School <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="school_id"
                      value={formData.school_id}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3.5 py-2.5 bg-white border border-purple-300 rounded-xl text-sm font-extrabold text-purple-950 focus:outline-none focus:ring-2 focus:ring-purple-600 transition"
                    >
                      <option value="">-- Select Target Client School --</option>
                      {schoolsList.map((s) => (
                        <option key={s.id} value={s.id}>
                          #{s.id} {s.name} ({s.plan_tier ? s.plan_tier.toUpperCase() : 'BASIC'})
                        </option>
                      ))}
                    </select>
                    <p className="text-[11px] text-purple-700">
                      This user will be given access to log into and manage this specific tenant school.
                    </p>
                  </div>
                )}

                {/* Student ID (Only shown if role === 'parent') */}
                {formData.role === 'parent' && (
                  <div className="p-4 bg-orange-50/60 border border-orange-200 rounded-2xl animate-fadeIn">
                    <label className="block text-xs font-semibold text-orange-900 mb-1.5">
                      Link Child Student <span className="text-xs text-orange-700 font-normal">(Parent Portal linkage)</span>
                    </label>
                    <select
                      name="student_id"
                      value={formData.student_id}
                      onChange={handleInputChange}
                      className="w-full px-3.5 py-2.5 bg-white border border-orange-200 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition"
                    >
                      <option value="">-- Select Linked Student --</option>
                      {students.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.student_name} (Roll: {s.roll_no || 'N/A'}, Class: {s.class_name || 'N/A'})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Is Active Toggle */}
                <div className="flex items-center justify-between p-3.5 bg-slate-50 border border-slate-200 rounded-xl">
                  <div>
                    <span className="block text-xs font-semibold text-slate-800">Account Active Status</span>
                    <span className="text-xs text-slate-500">Allow user to log in immediately</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                  </label>
                </div>

                {/* Submit Button */}
                <div className="pt-3">
                  <button
                    type="submit"
                    disabled={formSubmitting}
                    className="w-full py-3 bg-primary hover:bg-blue-600 text-white font-semibold rounded-xl shadow-md transition disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                  >
                    {formSubmitting ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                        <span>Creating User...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                        </svg>
                        <span>Create User Account</span>
                      </>
                    )}
                  </button>
                </div>

              </form>
            </div>
          </div>
        )}

      </main>

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 relative border border-slate-100">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 text-lg">Edit User Profile</h3>
              <button onClick={() => setEditingUser(null)} className="text-slate-400 hover:text-slate-600">
                &times;
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Full Name *</label>
                <input
                  type="text"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  required
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary font-medium"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Email Address / Username *</label>
                <input
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  required
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary font-medium text-slate-800"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Role *</label>
                  <select
                    value={editFormData.role}
                    onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
                    disabled={editingUser.id === currentUser?.id}
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 capitalize disabled:opacity-50 font-medium"
                  >
                    {availableRoles.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                  {editingUser.id === currentUser?.id && (
                    <span className="text-[10px] text-amber-600 mt-1 block">You cannot change your own role.</span>
                  )}
                </div>

                {currentUser?.role === 'super_admin' && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Assigned Tenant School *</label>
                    <select
                      value={editFormData.school_id}
                      onChange={(e) => setEditFormData({ ...editFormData, school_id: e.target.value })}
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 font-medium"
                    >
                      <option value="">-- Select School --</option>
                      {schoolsList.map((sc) => (
                        <option key={sc.id} value={sc.id}>
                          #{sc.id} {sc.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {editFormData.role === 'parent' && (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Linked Student</label>
                  <select
                    value={editFormData.student_id}
                    onChange={(e) => setEditFormData({ ...editFormData, student_id: e.target.value })}
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="">-- None --</option>
                    {students.map((s) => (
                      <option key={s.id} value={s.id}>{s.student_name} (Roll: {s.roll_no || 'N/A'})</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-xl">
                <span className="text-xs font-semibold text-slate-800 font-medium">Active Status</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editFormData.is_active}
                    onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.checked })}
                    disabled={editingUser.id === currentUser?.id}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-600 peer-disabled:opacity-50"></div>
                </label>
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setEditingUser(null)}
                  className="px-4 py-2 text-xs font-semibold text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 text-xs font-semibold text-white bg-primary hover:bg-blue-600 rounded-xl shadow-sm"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {deletingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 text-center border border-slate-100">
            <div className="w-12 h-12 rounded-full bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-4 border border-red-100">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <h3 className="font-bold text-slate-800 text-lg mb-1">Delete User Account?</h3>
            <p className="text-xs text-slate-500 mb-6">
              Are you sure you want to permanently delete user <span className="font-bold text-slate-700">{deletingUser.name}</span> ({deletingUser.email})? This action cannot be undone.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => setDeletingUser(null)}
                className="px-4 py-2 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                className="px-5 py-2 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl shadow-sm"
              >
                Permanently Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      <ResetPasswordModal
        isOpen={!!resetModalUser}
        onClose={() => setResetModalUser(null)}
        targetUser={resetModalUser}
        onSuccess={() => {
          setSuccessBanner(`Password for ${resetModalUser?.name} reset successfully.`);
          setTimeout(() => setSuccessBanner(''), 3000);
        }}
      />

    </div>
  );
};

export default UserManagement;
